import React, { useEffect, useState } from 'react';
import { checkHandoffLinks } from '../../api/twoDays/board.api';
import { cn } from './model/derive';
import { slotCheck } from './model/handoffSlots';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import { ReactComponent as CrossIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';

/**
 * 개발 → PM(QA) 인계 링크 확인 (포트폴리오 디벨롭 2단계 A1 · A2).
 *   A1 배포 링크가 열리는지 · 저장소가 공개인지 · README 가 있는지 — 실패(fail)면 제출을 막는다
 *   A2 README 필수 섹션 — 빠진 것만 보여 준다 (제출은 막지 않는다)
 * 결과 모양은 서버 TdLinkCheck.Result: { web, repo, readme: { status, message }, sections: { found, missing, template } }
 */

const ROWS = [
  ['web', 'Web link opens'],
  ['repo', 'Repository is public'],
  ['readme', 'README at the root'],
];

/** 목 보드 — 서버 없이 보이는 예시 결과 */
const MOCK_RESULT = {
  web: { status: 'ok', message: 'The link opens' },
  repo: { status: 'ok', message: 'Public repository' },
  readme: { status: 'ok', message: 'README found' },
  sections: {
    found: ['Problem and solution', 'How to run', 'Scope', 'Data', 'Tech stack'],
    missing: ['Open-source libraries', 'My role and contribution', 'AI usage', 'Known issues'],
    template: false,
  },
};

export const isBlocking = (result) => !!result && ROWS.some(([k]) => result[k]?.status === 'fail');

const Mark = ({ status }) => (
  <span
    aria-hidden
    className={cn(
      'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[11px] font-medium leading-none',
      status === 'ok' && 'bg-functional-success01 text-functional-success03',
      status === 'fail' && 'bg-functional-failed01 text-functional-failed03',
      status === 'unknown' && 'bg-grayscale-gray02 text-text-secondary',
    )}
  >
    {status === 'ok' ? (
      <CheckIcon className='h-3 w-3' />
    ) : status === 'fail' ? (
      <CrossIcon className='h-3 w-3' />
    ) : (
      '?'
    )}
  </span>
);

/** 결과 표 — 제출 창 · 태스크 상세(리뷰어)에서 같이 쓴다 */
export const LinkCheckResult = ({ result, className }) => {
  const s = result.sections;
  return (
    <div className={cn('flex flex-col gap-2 text-[13px] leading-[150%]', className)}>
      <ul className='flex flex-col gap-1.5'>
        {ROWS.map(([k, label]) => {
          const c = result[k] ?? { status: 'unknown', message: '' };
          return (
            <li key={k} className='flex items-start gap-2'>
              <Mark status={c.status} />
              <span className='min-w-0'>
                <span className='text-text-primary'>{label}</span>
                {c.status !== 'ok' && c.message && (
                  <span
                    className={cn(
                      'block',
                      c.status === 'fail' ? 'text-functional-failed03' : 'text-text-secondary',
                    )}
                  >
                    {c.message}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {s && (
        <div className='flex flex-col gap-1 border-t border-green-deep03/[0.08] pt-2'>
          <span className='font-medium text-text-primary'>
            README sections · {s.found.length} of {s.found.length + s.missing.length}
          </span>
          {s.missing.length > 0 && (
            <span className='text-text-secondary'>Missing: {s.missing.join(', ')}</span>
          )}
          {s.template && (
            <span className='text-functional-warning04'>
              Template guide lines or brackets are still in the README.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 제출 창의 확인 패널 — 배포 링크 · 저장소가 맞는 모양이면 잠깐 기다렸다 서버에 확인한다.
 * onState({ checking, result }) 로 제출 버튼을 막는다. 요청이 실패하면 막지 않는다(제출할 때 서버가 다시 본다).
 */
export const LinkCheckPanel = ({ live, slug, web, repo, onState }) => {
  const [state, setState] = useState({ checking: false, result: null, error: false });
  const ready = !!web && !!repo && !slotCheck('https', web) && !slotCheck('github', repo);

  useEffect(() => {
    if (!ready) {
      setState({ checking: false, result: null, error: false });
      return undefined;
    }
    let alive = true;
    setState((s) => ({ ...s, checking: true }));
    const timer = setTimeout(async () => {
      try {
        const result = live ? await checkHandoffLinks(slug, web, repo) : MOCK_RESULT;
        if (alive) setState({ checking: false, result, error: false });
      } catch {
        if (alive) setState({ checking: false, result: null, error: true });
      }
    }, 700);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [ready, live, slug, web, repo]);

  useEffect(() => {
    onState?.({ checking: state.checking, result: state.result });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.checking, state.result]);

  return (
    <div
      aria-live='polite'
      className='flex flex-col gap-2 rounded-xl bg-green-deep03/[0.04] px-4 py-3'
    >
      <span className='text-[13px] font-medium text-text-primary'>Link check</span>
      {!ready && (
        <span className='text-[13px] text-text-secondary'>
          Add the web link and repository. We check that they open before you submit.
        </span>
      )}
      {ready && state.checking && (
        <span className='text-[13px] text-text-secondary'>Checking your links…</span>
      )}
      {ready && !state.checking && state.error && (
        <span className='text-[13px] text-text-secondary'>
          Couldn’t check right now. You can still submit; we check again when you do.
        </span>
      )}
      {ready && !state.checking && state.result && <LinkCheckResult result={state.result} />}
    </div>
  );
};
